import {Component, computed, Inject, OnDestroy, OnInit, Optional, Signal} from '@angular/core';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { NgbActiveModal, NgbDate } from '@ng-bootstrap/ng-bootstrap';
import {combineLatest, merge, Subject} from 'rxjs';
import { filter, first, map, mergeAll, startWith, takeUntil, tap } from 'rxjs/operators';

import { FormConfigService } from 'src/app/form-config/form-config.service';
import { FIXING_TIME } from 'src/app/product-capture/model/consts/fixing-time.const';
import { FIXING_TYPE } from 'src/app/product-capture/model/consts/fixing-type.const';
import { FX_PAIR_FIXING_TYPE_ITEMS } from 'src/app/product-capture/model/consts/fx-pair-fixing-type.const';
import {
  PERFORMANCE_TYPES,
  PERFORMANCE_TYPE_KEYS,
} from 'src/app/product-capture/model/consts/performance-type.const';
import { ROLL_AS } from 'src/app/product-capture/model/consts/roll-as.const';
import { SETTLEMENT_TYPE } from 'src/app/product-capture/model/consts/settlement-type.const';
import { ProductTypeEnum } from 'src/app/product-capture/model/enums/product-capture.enums';
import { UnderlyingAsset } from 'src/app/product-capture/model/interfaces/underlying-asset.interface';
import { UnderlyingAssetsFE } from 'src/app/product-capture/model/interfaces/underlying-assets-fe.interface';
import { ProductCaptureService } from 'src/app/product-capture/services/product-capture.service';
import { FormatUnderlyingAssetLabel } from 'src/app/product-capture/utils/format-underlying-asset-label';
import { ProductUtils } from 'src/app/product-capture/utils/product.utils';
import { MODAL_CUSTOM_DATA } from '../../../../utils/create-modal-injector';
import { calculateBondUnderlyingAsset } from '../../../utils/calculate-num-deliverable-underlying-assets';
import { NumberUtils } from '../../../utils/number.utils';

export interface UnderlyingAssetsModalProps {
  productTypeValue: ProductTypeEnum,
  productCurrency: string,
  performanceType: string,
  settlementType: string,
  underlyingAssets: UnderlyingAsset[],
  rollAs: string,
  tradingCalendarsArrayAtOpen: string[],
  underlyingAssetsAtOpen: UnderlyingAsset[],
  legalForm: AbstractControl
}

export enum ModalMode {
  none,
  fxPair,
  bond
}

@Component({
  selector: 'app-underlying-assets-modal',
  templateUrl: './underlying-assets-modal.component.html',
  styleUrls: ['./underlying-assets-modal.component.scss'],
})
export class UnderlyingAssetsModalComponent implements OnInit, OnDestroy, UnderlyingAssetsModalProps {
  form: UntypedFormGroup;
  labels: Record<string, any>;
  performanceTypeItems = PERFORMANCE_TYPES;
  settlementTypeItems = SETTLEMENT_TYPE;
  rollAsItems = ROLL_AS;
  fixingTimeItems = FIXING_TIME;
  fixingTypeItems = FIXING_TYPE;
  fixingTypeFxPairItems = FX_PAIR_FIXING_TYPE_ITEMS;
  underlyingAssetsAllData: UnderlyingAsset[] = [];
  underlyingAssetsItems: UnderlyingAssetsFE[] = [];
  header: 'Underlying Assets' | 'FX Pair(s)' = 'Underlying Assets';

  productTypes = ProductTypeEnum;
  productTypeValue: ProductTypeEnum;
  productCurrency: string;
  performanceType: string;
  settlementType: string;
  rollAs: string;
  underlyingAssets: UnderlyingAsset[];
  loading = false;
  showInitialFixing = false;
  allowAddingDeletingAssets = true;
  allowMultipleAssets = true

  tradingCalendarsArrayAtOpen: string[] | [];
  underlyingAssetsAtOpen: UnderlyingAsset[] | [];
  destroy$ = new Subject<boolean>();
  legalForm: UntypedFormGroup;

  protected readonly ModalMode = ModalMode;

  private destroyAsset$ = new Subject<number>();

  private readonly PRODUCT_TYPES_WITH_NO_INITIAL_FIXING = [
    ProductTypeEnum.CHOOSER,
    ProductTypeEnum.CLN,
    ProductTypeEnum.FIXED_RATE_NOTE,
    ProductTypeEnum.FIXED_TO_FLOATING_FLOATING_TO_FIXED_RATE_NOTE,
    ProductTypeEnum.FLOATING_RATE_NOTE,
    ProductTypeEnum.FX_DIGITAL_COUPON_NOTE,
  ];

  constructor(
    private formConfigService: FormConfigService,
    public modal: NgbActiveModal,
    private fb: UntypedFormBuilder,
    private productCaptureService: ProductCaptureService,
    @Inject(MODAL_CUSTOM_DATA) @Optional() props: Partial<UnderlyingAssetsModalProps>
  ) {
    if (props) {
      Object.assign(this, {...props});
    }

    this.formConfigService.setFormConfig('underlyingAssetsModal');
    this.form = this.fb.group({
      performanceType: [],
      settlementType: [],
      rollAs: [],
      underlyingAssets: this.fb.array([]),
    });
  }

  ngOnInit(): void {
    this.loading = true;
    this.labels = this.formConfigService.getLabels();

    this.form.patchValue({
      performanceType: this.performanceType,
      settlementType: this.settlementType,
      rollAs: this.rollAs,
    });

    this.header = this.modalMode() === ModalMode.fxPair ? 'FX Pair(s)' : 'Underlying Assets';

    this.getUnderlyingAssetsOptions();
    setTimeout(() => {
      this.formConfigService.start(this.form, 'underlyingAssetsModal', true);
    }, 0);
    this.form
      .get('underlyingAssets')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((arr) => {
        if (arr.length > 1) {
          this.resetPerformanceType();
        }
      });

    if (this.modalMode() === ModalMode.fxPair) {
      this.setFxPairSettings();
    }

    if (
      !this.PRODUCT_TYPES_WITH_NO_INITIAL_FIXING.includes(this.productTypeValue)
    ) {
      this.showInitialFixing = true;
    }

    this.allowMultipleAssets = ProductUtils.allowMultipleAssets(
      this.productTypeValue
    );

    if (this.productTypeValue === ProductTypeEnum.CHOOSER) {
      this.allowAddingDeletingAssets = false;
    }
  }

  ngOnDestroy(): void {
    this.formConfigService.stop('underlyingAssetsModal');
    this.destroy$.next(true);
    this.destroy$.complete();
    this.destroyAsset$.complete();
  }

  modalMode: Signal<ModalMode> = computed(() => {
    if (ProductUtils.isFxPair(this.productTypeValue)) {
      return ModalMode.fxPair;
    }

    if (ProductUtils.isBond(this.productTypeValue)) {
      return ModalMode.bond;
    }

    return ModalMode.none;
  })

  setFxPairSettings(): void {
    this.form.get('performanceType')['readOnly'] =
      this.modalMode() === ModalMode.fxPair && this.productTypeValue !== ProductTypeEnum.CHOOSER;
    this.form.get('settlementType')['readOnly'] = true;
    this.form.get('rollAs').setValue('INDIVIDUAL');
    this.form.get('rollAs').disable();
  }

  resetPerformanceType(): void {
    const performanceType = this.form.get('performanceType').value;
    if (performanceType === PERFORMANCE_TYPE_KEYS.SINGLE) {
      this.form.get('performanceType').setValue(null);
    }
    return;
  }

  // Dynamic form
  get assets(): UntypedFormArray {
    return this.form.get('underlyingAssets') as UntypedFormArray;
  }

  addAsset(value: UnderlyingAsset, index?: number): void {
    const initialFixingDate = this.getNgbDate(value?.initial_FixingDate);

    const assetGroup = this.fb.group({
      type: [value?.type || ''],
      assetId: [value?.assetId || ''],
      exchange: [value?.exchange || ''],
      currency: [value?.currency || ''],
      calendars: [value?.calendars || []],
      initial_FixingDate: [initialFixingDate || null],
      fixingType: [value?.fixingType || this.fixingTypeDefault()],
      weight: [value?.weight || 0],
      observationTime: [value?.observationTime || 'AT_CLOSE'],
      observationLag: [value?.observationLag || null],
      sourcePage: [value?.sourcePage || null],
      fixingTime: [value?.fixingTime || null],
      initialFixing: [value?.initialFixing || null],
      couponStrike: [value?.couponStrike || null],
      redemptionStrike: [value?.redemptionStrike || null],
      initialStrikeClean: [value?.initialStrikeClean || null],
      initialStrikeDirty: [value?.initialStrikeDirty || null],
      forwardAccruedCoupon: [value?.forwardAccruedCoupon || null],
      numDeliverableUnderlyingAssets: [value?.numDeliverableUnderlyingAssets || null]
    });

    if (!this.showInitialFixing) {
      assetGroup.get('initialFixing').disable();
    }

    this.assets.push(assetGroup);
    this.nullifyRow0Weight(index, this.assets?.at(0)?.get('weight')?.value);

    if (this.modalMode() === ModalMode.bond) {
      this.calculateBondFields(assetGroup, index);
    }
  }

  calculateBondFields(assetForm: UntypedFormGroup, index: number): void {
    const destroy$ = merge([
      this.destroy$,
      this.destroyAsset$
        .pipe(filter(destroyId => destroyId === index))
    ])
      .pipe(mergeAll());

    combineLatest([
      assetForm.get('initialStrikeClean')!.valueChanges
        .pipe(startWith(assetForm.get('initialStrikeClean').value)),
      assetForm.get('forwardAccruedCoupon')!.valueChanges
        .pipe(startWith(assetForm.get('forwardAccruedCoupon').value))
    ])
      .pipe(
        tap(() => {
          assetForm.get('initialStrikeDirty')!
            .setValue({
              value: null,
              indicativeValue: null,
              minValue: null,
              maxValue: null
            });
        }),
        map(([initialStrikeClean, forwardAccruedCoupon]) => {
          return [
            NumberUtils.stringToNumber(initialStrikeClean?.value),
            NumberUtils.stringToNumber(forwardAccruedCoupon?.value)
          ]
        }),
        filter(([initialStrikeClean,
                  forwardAccruedCoupon]) => Number.isFinite(initialStrikeClean) &&
          Number.isFinite(forwardAccruedCoupon)),
        takeUntil(destroy$)
      )
      .subscribe({
        next: ([initialStrikeClean, forwardAccruedCoupon]) => {
          assetForm.get('initialStrikeDirty')!
            .setValue({
              value: `${initialStrikeClean + forwardAccruedCoupon}`,
              indicativeValue: null,
              minValue: null,
              maxValue: null
            });
        }
      });

    combineLatest([
      assetForm.get('initialStrikeDirty')!.valueChanges
      .pipe(startWith(assetForm.get('initialStrikeDirty').value)),
      assetForm.get('assetId')!.valueChanges
        .pipe(
          startWith(assetForm.get('assetId').value),
          map(assetId => this.underlyingAssetsAllData
            .find(item => item.assetId === assetId))
        )
    ])
      .pipe(
        tap(() => {
          assetForm.get('numDeliverableUnderlyingAssets')!
            .setValue(null);
        }),
        map(([initialStrikeDirty, underlyingAsset]) => {
          return [
            NumberUtils.stringToNumber(initialStrikeDirty?.value) / 100,
            NumberUtils.stringToNumber(underlyingAsset?.denomination),
            NumberUtils.stringToNumber(this.legalForm.get('legal.denominationLegal').value ?? 0)
          ]
        }),
        filter(([initialStrikeDirty,
                  underlyingAssetDenomination,
                  legalDenomination]) => Number.isFinite(initialStrikeDirty) &&
          Number.isFinite(underlyingAssetDenomination) &&
          Number.isFinite(legalDenomination)),
        takeUntil(destroy$)
      )
      .subscribe({
        next: ([initialStrikeDirty,
                 underlyingAssetDenomination,
                 legalDenomination]) => {
          if (initialStrikeDirty && underlyingAssetDenomination && legalDenomination) {
            const numDeliverableUnderlyingAssets = calculateBondUnderlyingAsset(initialStrikeDirty, legalDenomination, underlyingAssetDenomination);
            assetForm.get('numDeliverableUnderlyingAssets')!.setValue(numDeliverableUnderlyingAssets);
          }
        }
      })
  }

  fixingTypeDefault(): string {
    return this.modalMode() === ModalMode.fxPair ? 'MID' : 'AT_CLOSE';
  }

  nullifyRow0Weight(index: number, row0WeightValue: number | string): void {
    if (index === 1 && (row0WeightValue === 1 || row0WeightValue === '1')) {
      this.assets.at(0).get('weight').setValue(0);
    } else return;
  }

  generateAssetsRows(): void {
    if (this.underlyingAssets?.length) {
      this.underlyingAssets.forEach((asset) => this.addAsset(asset));
    } else {
      this.addAsset(null);
      this.assets.at(0).get('weight').setValue(1);

      if (this.productTypeValue === ProductTypeEnum.CHOOSER) {
        this.addAsset(null);

        this.assets.at(0).get('assetId').addValidators([Validators.required]);
        this.assets.at(1).get('assetId').addValidators([Validators.required]);
        this.assets.at(1).get('weight').setValue(1);
      }
    }
  }

  deleteAsset(index: number): void {
    this.destroyAsset$.next(index);
    if (this.assets.value.length <= 1) {
      this.form.controls.underlyingAssets.reset();
      this.calculateBondFields(this.assets.at(0) as UntypedFormGroup, 0);
    } else {
      this.assets.removeAt(index);
    }
  }

  hasAssetIndexType(): boolean {
    return this.form.controls.underlyingAssets.value?.some((asset) => {
      const isIndex = asset.type === 'INDEX';
      return isIndex;
    });
  }

  isSettlementTypePhysical(): boolean {
    return this.form.controls.settlementType.value === 'PHYSICAL';
  }

  isSettlementWarning(): boolean {
    return this.isSettlementTypePhysical() && this.hasAssetIndexType();
  }

  // Modal: clear, save
  clearModal(): void {
    this.modal.close('cancel');
  }

  save(): void {
    this.form
      .get('underlyingAssets')
      .updateValueAndValidity({ emitEvent: true });

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const underlyingAssets = this.form.get('underlyingAssets').value;
    if (this.modalMode() !== ModalMode.bond) {
      underlyingAssets.forEach((asset) => {
        asset.initial_FixingDate = this.fromNgbDate(asset?.initial_FixingDate);

        if (this.performanceType === PERFORMANCE_TYPE_KEYS.BASKET) {
          asset.weight = Number(asset.weight) ?? 1;
        }
      });
    }

    const payload = {
      performanceType: this.form.get('performanceType').value,
      settlementType: this.form.get('settlementType').value,
      rollAs: this.form.get('rollAs').value,
      underlyingAssets: underlyingAssets.filter((asset) => asset.assetId),
      tradingCalendarsArrayAtOpen: this.tradingCalendarsArrayAtOpen,
      underlyingAssetsAtOpen: this.underlyingAssetsAtOpen,
    };
    this.modal.close(payload);
  }

  // Underlying assets typeahead data
  private getUnderlyingAssetsOptions() {
    return this.productCaptureService
      .getUnderlyingAssets()
      .pipe(first())
      .subscribe(
        (underlyingAssets) => {
          this.underlyingAssetsAllData = underlyingAssets;

          if (
            underlyingAssets?.length > 0 &&
            !this.underlyingAssetsItems?.length
          ) {
            this.underlyingAssetsItems = underlyingAssets
              .filter((asset) => this.modalMode() !== ModalMode.bond ||
                (this.modalMode() === ModalMode.bond && asset.type === 'BOND'))
              .map((asset) => {
                return {
                  id: asset.assetId,
                  displayValue: FormatUnderlyingAssetLabel(asset),
                  type: asset.type,
                  currency: asset.currency,
                };
              });
          }
          this.generateAssetsRows();
          this.loading = false;
        },
        (error) => {
          console.log(error);
          this.generateAssetsRows();
          this.loading = false;
        }
      );
  }

  // Readonly field updates
  updateReadonlyFields(selected: string, i: number): void {
    const asset = this.getReadonlyDataFromUnderlyingAssetsArr(selected);
    if (asset) {
      this.assets.at(i).get('type').setValue(asset.type);
      this.assets.at(i).get('exchange').setValue(asset.exchange);
      this.assets.at(i).get('currency').setValue(asset.currency);
      this.assets.at(i).get('observationTime').setValue('AT_CLOSE');
      this.assets.at(i).get('observationLag').setValue(null);
      this.assets.at(i).get('calendars').setValue(asset.calendars);
      this.assets
        .at(i)
        .get('sourcePage')
        .setValue(asset.sourcePage ?? null);
      this.assets
        .at(i)
        .get('fixingTime')
        .setValue(asset.fixingTime ?? null);
    }
  }

  getReadonlyDataFromUnderlyingAssetsArr(assetId: string): UnderlyingAsset {
    const out = this.underlyingAssetsAllData.find((obj) => {
      return obj.assetId === assetId;
    });
    return out;
  }

  private fromNgbDate(object: NgbDate): string {
    if (!object) {
      return null;
    } else {
      const day = object?.day < 10 ? '0' + object.day : object.day;
      const month = object?.month < 10 ? '0' + object.month : object.month;
      const year = object?.year;

      return object?.day ? `${year}-${month}-${day}` : null;
    }
  }

  private getNgbDate(date: string): NgbDate {
    const dateArray = date?.split('-');

    return dateArray
      ? new NgbDate(
          Number(dateArray[0]),
          Number(dateArray[1]),
          Number(dateArray[2]?.substring(0, 2))
        )
      : null;
  }
}
